import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useStore } from 'react-redux'
import history from 'browserHistory'
import { isDetachedMode } from 'helpers/URLHelper'
import Helmet from 'react-helmet'
import './style.css'

import enhance from './enhance'
import { selectors as NotesSelectors } from 'data/notes'

import NotesBulkControls from 'components/Notes/BulkControls'
import NoteView, {
  CENTERED_CONTENT_CLASSNAME,
} from 'components/Layout/NoteView'
import { NotePanelLoading } from 'components/Layout/AppLoading'
import { NotFound, Forbidden } from 'components/Errors/Note'
import NoConnection from 'components/Errors/InAppNoConnection'
import { Provider as SharedEventBusContextProvider } from 'contexts/sharedEventBus'
import RichTextEditor from './RichTextEditor'

import NotesShowHeader from 'screens/NotesShowHeader'
import NoteTitle from 'components/Notes/Title'
import RoutesHelper from 'helpers/RoutesHelper'
import { getRouteParams, getActiveChannelId } from 'data/ui/selectors'

function NotesShowScreen({
  noteId,
  noteTitle,
  isDirectEditingNote = false,
  noteIsLockedForEdition = false,
  channelLabel,
  userCanEditNote,
  errors,
  showLoadingPlaceholder,
  onTitleChangeSuccess,
}) {
  const [scrolling, setScrolling] = React.useState(false)
  const [editorRef, setEditorRef] = React.useState(null)
  const titleInputRef = React.useRef()

  useNoteContextRedirector(noteId)

  const focusEditor = React.useCallback(
    ({ at = 0, insertLine = false } = {}) => {
      if (editorRef && editorRef.focusFirstLine) {
        editorRef.focusFirstLine(at, { insertLine })
      }
    },
    [editorRef]
  )

  const focusNoteTitleInput = React.useCallback(
    ({ at = 0 }) => {
      if (Boolean(titleInputRef.current)) {
        titleInputRef.current.focus()
        if (titleInputRef.current.setSelectionRange && at >= 0) {
          requestAnimationFrame(() => {
            titleInputRef.current.setSelectionRange(at, at)
          })
        }
      }
    },
    [titleInputRef]
  )

  // We delay the rendering to prevent dropping frames
  const isIdle = useIdleOnSwitch({ id: noteId })

  if (errors && errors[0]) {
    return <Errors errors={errors} noteId={noteId} />
  }

  if (showLoadingPlaceholder) {
    return <NotePanelLoading />
  }

  const noteIsEditable = !noteIsLockedForEdition || isDirectEditingNote

  return (
    <React.Fragment>
      <Helmet title={`${noteTitle || 'untitled'} - ${channelLabel}`} />
      <NotesBulkControls>
        <SharedEventBusContextProvider>
          <NoteView
            setScrolling={setScrolling}
            header={<NotesShowHeader noteId={noteId} scrolling={scrolling} />}
            contentClassName={CENTERED_CONTENT_CLASSNAME}
          >
            <NoteTitle
              ref={titleInputRef}
              noteId={noteId}
              noteTitle={noteTitle}
              focusNext={focusEditor}
              readOnly={!noteIsEditable || !userCanEditNote}
            />

            {!isIdle && (
              <RichTextEditor
                noteId={noteId}
                noteTitle={noteTitle}
                userCanEdit={noteIsEditable && userCanEditNote}
                onTitleChangeSuccess={onTitleChangeSuccess}
                onEditorRef={setEditorRef}
                focusNoteTitleInput={focusNoteTitleInput}
              />
            )}
          </NoteView>
        </SharedEventBusContextProvider>
      </NotesBulkControls>
    </React.Fragment>
  )
}

NotesShowScreen.propTypes = {
  noteId: PropTypes.string,
  noteTitle: PropTypes.string,
  channelLabel: PropTypes.string,

  userCanEditNote: PropTypes.bool.isRequired,

  showLoadingPlaceholder: PropTypes.bool.isRequired,
  isCommentsOpen: PropTypes.bool.isRequired,
  isOutlineOpen: PropTypes.bool.isRequired,
}

export default enhance(React.memo(NotesShowScreen))

function Errors({ noteId, errors }) {
  const error = Array.isArray(errors[0].errors)
    ? errors[0].errors[0]
    : errors[0].errors

  const { status, id } = error || {}

  if (status === 403) {
    return React.createElement(Forbidden, {
      params: {
        noteId,
        isPersonal:
          id === 'channel/access-forbidden/personal-without-ownership',
      },
    })
  } else if (status === 404) {
    return React.createElement(NotFound, {
      params: { noteId },
    })
  }
  if (window.bugsnag) {
    window.bugsnag.notify(new Error('Unknown error in sync doc return'), {
      metaData: {
        errorMessage: errors[0].message,
      },
    })
  }
  return <NoConnection />
}

function useIdleOnSwitch({ id = null, timeout = 100 }) {
  const idRef = React.useRef(id)
  const [isIdle, setIsIdle] = React.useState(true)
  React.useEffect(
    () => {
      idRef.current = id
      setIsIdle(true)
      const idleTimeout = setTimeout(() => {
        setIsIdle(false)
      }, timeout)
      return () => {
        clearTimeout(idleTimeout)
      }
    },
    [id, timeout]
  )
  return isIdle && idRef.current === id
}

function useNoteContextRedirector(noteId) {
  const store = useStore()
  const noteChannelId = useSelector(state => {
    const note = NotesSelectors.getNote(state, noteId)
    return note && note.channelId
  })
  const urlChannelId = useSelector(getActiveChannelId)
  const routeNoteId = useSelector(state => getRouteParams(state).noteId)

  React.useEffect(
    () => {
      const {
        filterId,
        options,
        from,
        threadId,
        commentId,
        blockId,
      } = getRouteParams(store.getState())

      if (!noteChannelId) {
        return // don't change things until we know where to go
      }
      if (routeNoteId !== noteId) {
        return // don't change if the note in route params is not the one displayed
      }
      if (filterId) {
        return // don't do anything if we're in a filter
      }

      const urlChannelIdMissingOrMismatch =
        !urlChannelId || urlChannelId !== noteChannelId

      if (urlChannelIdMissingOrMismatch) {
        history.replace(
          RoutesHelper.noteChannelShow({
            channelId: noteChannelId,
            noteId,
            // Maintain Detached mode over channel switching
            options: isDetachedMode() ? 'detached' : options,
            // Prevent back button when redirecting for the same note
            from: from === noteId ? null : from,
            threadId,
            commentId,
            blockId,
          })
        )
      }
    },
    [store, noteId, noteChannelId, routeNoteId, urlChannelId]
  )
}
